import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthToken } from '../hooks/useAuthToken';
import useAnalyticsStatus from '../hooks/useAnalyticsStatus';
import ConnectGoogleAnalytics from '../components/analytics/ConnectGoogleAnalytics';
import PropertySelector from '../components/analytics/PropertySelector';
import AnalyticsDashboard from '../components/analytics/AnalyticsDashboard';
import Toast, { useToast } from '../components/analytics/Toast';
import authService from '../services/authService';
import '../styles/analytics-variables.css';
import './AnalyticsPage.css';

const AnalyticsPage = () => {
  const [setupComplete, setSetupComplete] = useState(false);
  const [noGAAccount, setNoGAAccount] = useState(false);
  const navigate = useNavigate();
  const { token, isLoading } = useAuthToken();
  const { status, analyticsInfo, refetch } = useAnalyticsStatus();
  const { toasts, showToast } = useToast();

  // Check URL parameters on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('connected') === 'true') {
      if (params.get('setup') === 'no_ga_account') {
        setNoGAAccount(true);
      } else {
        // Normal flow — refresh status
        refetch();
      }
      // Clean the URL
      window.history.replaceState({}, '', '/analytics');
    }
  }, [refetch]);

  // Add token guard - wait for token before rendering anything
  if (isLoading || !token) {
    return (
      <div className="ga-loading-center">
        <div className="ga-spinner-lg"></div>
        {/* Debug button for development */}
        {process.env.NODE_ENV === 'development' && !isLoading && (
          <div style={{ marginTop: '20px' }}>
            <button 
              onClick={() => {
                authService.clearToken();
                window.location.reload();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear Token & Reload
            </button>
          </div>
        )}
      </div>
    );
  }

  const handleDisconnect = () => {
    refetch();
  };

  const handleSetupComplete = () => {
    setSetupComplete(true);
    refetch();
  };

  const renderContent = () => {
    if (status === 'loading') {
      return (
        <div className="ga-loading-center">
          <div className="ga-spinner-lg"></div>
        </div>
      );
    }

    if (status === 'error') {
      return (
        <div className="ga-error-center">
          <svg className="ga-error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ga-error-text">Could not load analytics status</div>
          <button className="ga-retry-page-btn" onClick={refetch}>
            Retry
          </button>
          {/* Debug button for development */}
          {process.env.NODE_ENV === 'development' && (
            <button 
              onClick={() => {
                authService.clearToken();
                window.location.reload();
              }}
              style={{
                marginTop: '10px',
                padding: '8px 16px',
                backgroundColor: '#ff4444',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Clear Token & Reload
            </button>
          )}
        </div>
      );
    }

    if (status === 'disconnected') {
      return <ConnectGoogleAnalytics />;
    }

    if (status === 'connected' && (!analyticsInfo?.propertyId || setupComplete === false && analyticsInfo.propertyId === "")) {
      return <PropertySelector onSetupComplete={handleSetupComplete} initialNoGAAccount={noGAAccount} />;
    }

    if (status === 'connected' && analyticsInfo?.propertyId) {
      return <AnalyticsDashboard onDisconnect={handleDisconnect} />;
    }

    return null;
  };

  return (
    <div className="ga-page-wrapper">
      {/* Back button */}
      <button
        onClick={() => navigate('/analytics-hub')}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          background: 'transparent',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '6px 14px',
          cursor: 'pointer',
          color: '#6b7280',
          fontSize: '13px',
          fontWeight: '500',
          marginBottom: '12px',
          transition: 'all 0.15s'
        }}
        onMouseEnter={e => {
          e.currentTarget.style.background = '#f9fafb';
          e.currentTarget.style.color = '#374151';
        }}
        onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = '#6b7280';
        }}
      >
        ← Back to Dashboard
      </button>
      
      <div className="ga-page-header">
        <svg className="ga-page-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
        <h1 className="ga-page-title">Analytics</h1>
      </div>
      
      {renderContent()}
      
      <Toast toasts={toasts} />
    </div>
  );
};

export default AnalyticsPage;